import React, { useState } from "react";
import { addWeeks, startOfDay } from "date-fns";

import { MeetingList } from "./components/MeetingList";

import {
  Stack,
  Box,
  SelectChangeEvent,
  Divider,
  LinearProgress,
  Typography,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import SyncIcon from "@mui/icons-material/Sync";
import {
  FilterTypes,
  Filters,
  defaultFilters,
} from "./components/Filters";
import { MeetingDetailsContainer } from "./components/meeting_details/MeetingDetails";
import { useBoundStore } from "../../store/store";
import { Meeting, isMissingData } from "../../model/meeting";
import { MeetingResource } from "../../api/meetings";

const SYNC_COOLDOWN_MS = 60_000;
const SYNC_COOLDOWN_KEY = "calendarSyncLastTriggered";

function isActiveFilters(filters: FilterTypes): boolean {
  return (
    (filters.search ?? "") !== "" ||
    (filters.meetingType ?? "All") !== "All" ||
    (filters.status ?? "All") !== "All" ||
    (filters.hostId ?? "All") !== "All"
  );
}

function getCooldownRemaining(): number {
  const last = localStorage.getItem(SYNC_COOLDOWN_KEY);
  if (!last) return 0;
  const elapsed = Date.now() - parseInt(last, 10);
  return Math.max(0, SYNC_COOLDOWN_MS - elapsed);
}

export const MeetingListView = () => {
  const meetings = useBoundStore((state) => state.meetings);
  const hostsById = useBoundStore((state) => state.hostsById);
  const loadMeetings = useBoundStore((state) => state.loadMeetings);
  const loadMeetingTypes = useBoundStore((state) => state.loadMeetingTypes);
  const isLoading = useBoundStore((state) => state.isLoading);
  const meetingsStart = useBoundStore((state) => state.start);
  const meetingsEnd = useBoundStore((state) => state.end);

  const [filteredMeetings, setFilteredMeetings] = useState<Meeting[]>([]);
  const [filters, setFilters] = useState<FilterTypes>(defaultFilters);
  const [selectedMeetingId, setSelectedMeetingId] = useState<string | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  const [isSyncing, setIsSyncing] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(getCooldownRemaining);
  const [syncSnackbar, setSyncSnackbar] = useState<{
    open: boolean;
    severity: "success" | "error";
    message: string;
  }>({ open: false, severity: "success", message: "" });

  // Derive both selectedMeeting and selectedIdx from ID so they stay in sync
  // with the store after background reloads — no stale positional index.
  const activeFilters = isActiveFilters(filters);
  const activeList = activeFilters ? filteredMeetings : meetings;
  const selectedMeeting =
    meetings.find((m) => m.id === selectedMeetingId) ?? meetings[0];
  const selectedIdx = Math.max(
    0,
    activeList.findIndex((m) => m.id === selectedMeetingId)
  );

  function handleSelectMeeting(_idx: number, mId: string) {
    setSelectedMeetingId(mId);
  }

  function handleUpdateFilter(
    value: string | undefined,
    filter: keyof FilterTypes
  ) {
    setFilters({ ...filters, [filter]: value });
  }

  React.useEffect(() => {
    async function loadData() {
      const now = new Date();
      const start = startOfDay(now);
      const end = addWeeks(start, 4);
      await Promise.all([loadMeetings(start, end), loadMeetingTypes()]);
      setHasLoaded(true);
    }

    loadData();
  }, []);

  React.useEffect(() => {
    if (meetings.length > 0 && !selectedMeetingId) {
      setSelectedMeetingId(meetings[0].id);
    }
  }, [meetings]);

  React.useEffect(() => {
    if (!isActiveFilters(filters)) return;

    let result = [...meetings];

    if (filters.search && filters.search !== "All") {
      result = result.filter((m) =>
        m.title?.toLowerCase().includes(filters.search!.toLowerCase())
      );
    }

    if (filters.meetingType && filters.meetingType !== "All") {
      result = result.filter(
        (m) => m.metadata.meetingType === filters.meetingType
      );
    }

    if (filters.status && filters.status !== "All") {
      if (filters.status === "NotVisible") {
        result = result.filter((m) => !m.metadata.memberVisible);
      }
      if (filters.status === "Missing data") {
        result = result.filter((m) => isMissingData(m));
      }
    }

    if (filters.hostId && filters.hostId !== "All") {
      result = result.filter((m) => m.host?.id === filters.hostId);
    }

    setFilteredMeetings(result);
  }, [filters, meetings]);

  // Tick cooldown counter down every second while active
  React.useEffect(() => {
    if (cooldownRemaining <= 0) return;
    const interval = setInterval(() => {
      const remaining = getCooldownRemaining();
      setCooldownRemaining(remaining);
      if (remaining <= 0) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, [cooldownRemaining]);

  async function handleSyncNow() {
    if (isSyncing || cooldownRemaining > 0) return;
    setIsSyncing(true);
    try {
      await MeetingResource.triggerCalendarSync();
      localStorage.setItem(SYNC_COOLDOWN_KEY, String(Date.now()));
      setCooldownRemaining(SYNC_COOLDOWN_MS);
      await loadMeetings(meetingsStart, meetingsEnd);
      setSyncSnackbar({ open: true, severity: "success", message: "Sync complete — meetings updated." });
    } catch {
      setSyncSnackbar({ open: true, severity: "error", message: "Sync failed. Try again later." });
    } finally {
      setIsSyncing(false);
    }
  }

  const noResultsMatchingFilters = activeFilters && !filteredMeetings.length;

  const hostList = Object.entries(hostsById).map(([id, host]) => ({
    id,
    name: host.displayName,
  }));
  hostList.sort((a, b) => a.name.localeCompare(b.name));

  const cooldownSecs = Math.ceil(cooldownRemaining / 1000);
  const syncDisabled = isSyncing || cooldownRemaining > 0;

  return (
    <Stack gap={1} sx={{ marginTop: "20px", width: "100%" }}>
      {isLoading && <LinearProgress />}

      <Stack direction="row" justifyContent="flex-end" sx={{ px: 1 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={
            isSyncing ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <SyncIcon fontSize="small" />
            )
          }
          disabled={syncDisabled}
          onClick={handleSyncNow}
        >
          {isSyncing
            ? "Syncing..."
            : cooldownRemaining > 0
            ? `Sync Now (${cooldownSecs}s)`
            : "Sync Now"}
        </Button>
      </Stack>

      {!meetings.length && hasLoaded && !isLoading ? (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">No meetings found</Typography>
        </Box>
      ) : meetings.length > 0 ? (
        <>
          <Filters
            hosts={hostList}
            filters={filters}
            hasFilters={activeFilters}
            onClearFilters={() => setFilters(defaultFilters)}
            onMeetingTypeChange={(meetingTypeId: string) =>
              handleUpdateFilter(meetingTypeId, "meetingType")
            }
            onSearchChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleUpdateFilter(e.target.value, "search")
            }
            onStatusChange={(e: SelectChangeEvent) =>
              handleUpdateFilter(e.target.value, "status")
            }
            onHostChange={(e: SelectChangeEvent) => {
              handleUpdateFilter(e.target.value as string, "hostId");
            }}
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              width: "100%",
              bgcolor: "background.paper",
              height: "100%",
            }}
          >
            {noResultsMatchingFilters ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                  height: "100%",
                }}
              >
                <h2>No meetings found</h2>
              </Box>
            ) : (
              <MeetingList
                meetings={activeList}
                onSelectMeeting={handleSelectMeeting}
                selectedIdx={selectedIdx}
              />
            )}
            {!noResultsMatchingFilters && selectedMeeting && (
              <>
                <Divider orientation="vertical" flexItem />
                <MeetingDetailsContainer
                  key={selectedMeeting.id}
                  selectedMeeting={selectedMeeting}
                />
              </>
            )}
          </Box>
        </>
      ) : null}

      <Snackbar
        open={syncSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setSyncSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={syncSnackbar.severity}
          onClose={() => setSyncSnackbar((s) => ({ ...s, open: false }))}
        >
          {syncSnackbar.message}
        </Alert>
      </Snackbar>
    </Stack>
  );
};

export const CalendarManagement = () => {
  return <MeetingListView />;
};
